import React, { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  adminCourseProgressReportAction,
  adminUserCourseProgressAction,
  adminUserCourseWeekSectionsAction
} from "../Action/action";
import config from "../coreFIles/config";

const BBA_COURSE_ID = 8;

function formatDate(value) {
  if (!value) return "—";
  const s = String(value).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
  }
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function titleCase(value) {
  if (value === undefined || value === null) return "—";
  const s = String(value).trim();
  if (!s || s === "—") return "—";
  if (s.includes("@")) return s;
  return s
    .replace(/[_]+/g, " ")
    .split(/(\s+)/)
    .map((part) => {
      if (!part.trim()) return part;
      return part
        .split("-")
        .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word))
        .join("-");
    })
    .join("");
}

function userLabel(row) {
  if (!row) return "—";
  if (row.user_name) return titleCase(row.user_name);
  if (row.user_email) return String(row.user_email);
  if (row.user_id) return `User #${row.user_id}`;
  return "—";
}

function isOnboardingPending(row) {
  return String(row?.onboarding_status || "").toLowerCase() !== "completed";
}

function isCourseCompleted(row) {
  return String(row?.progress_status || "").toLowerCase() === "completed";
}

function progressBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "badge-success";
  if (s === "waiting" || s === "in_progress") return "badge-warning";
  if (s === "ready_to_unlock") return "badge-danger";
  return "badge-muted";
}

function weekOptionLabel(week) {
  return titleCase(week?.title || week?.week_title);
}

function resolvePublicUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  let origin = "";
  try {
    if (config.apiUrl) origin = new URL(config.apiUrl).origin;
  } catch (e) {
    origin = "";
  }
  if (!origin) origin = "http://localhost:3001";
  if (s.startsWith("/")) return `${origin}${s}`;
  return `${origin}/${s}`;
}

function SmoothCollapse({ open, children }) {
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const mountedRef = useRef(false);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const inner = innerRef.current;
    if (!wrap || !inner) return undefined;

    const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    if (!mountedRef.current) {
      mountedRef.current = true;
      wrap.style.transition = prefersReduced ? "none" : "";
      wrap.style.height = open ? "auto" : "0px";
      wrap.style.opacity = open ? "1" : "0";
      wrap.style.pointerEvents = open ? "auto" : "none";
      return undefined;
    }

    if (prefersReduced) {
      wrap.style.transition = "none";
      wrap.style.height = open ? "auto" : "0px";
      wrap.style.opacity = open ? "1" : "0";
      wrap.style.pointerEvents = open ? "auto" : "none";
      return undefined;
    }

    if (open) {
      wrap.style.height = "0px";
      wrap.style.opacity = "0";
      const target = inner.scrollHeight;
      void wrap.offsetHeight;
      wrap.style.pointerEvents = "auto";
      wrap.style.height = `${target}px`;
      wrap.style.opacity = "1";
      const onEnd = (event) => {
        if (event.propertyName !== "height") return;
        wrap.style.height = "auto";
      };
      wrap.addEventListener("transitionend", onEnd);
      return () => wrap.removeEventListener("transitionend", onEnd);
    }

    const currentHeight = wrap.getBoundingClientRect().height || inner.scrollHeight;
    wrap.style.height = `${currentHeight}px`;
    wrap.style.opacity = "1";
    void wrap.offsetHeight;
    wrap.style.pointerEvents = "none";
    wrap.style.height = "0px";
    wrap.style.opacity = "0";
    return undefined;
  }, [open]);

  return (
    <div ref={wrapRef} className="row-expand-anim" aria-hidden={!open}>
      <div ref={innerRef} className="row-expand-anim-inner">
        {children}
      </div>
    </div>
  );
}

function SectionMediaTable({ section }) {
  const media = Array.isArray(section?.media) ? section.media : [];
  const isVideos = String(section?.section_type || "").toLowerCase() === "videos";
  return (
    <div className="nested-table-wrap">
      <h4 style={{ marginBottom: 8, fontSize: 16 }}>{isVideos ? "Videos" : "Resources"}</h4>
      <table className="nested-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Title</th>
            <th>Type</th>
            <th>Viewed</th>
            {!isVideos ? <th>Read</th> : null}
          </tr>
        </thead>
        <tbody>
          {media.length ? (
            media.map((m, idx) => (
              <tr key={m.id}>
                <td>{m.position != null ? m.position : idx + 1}</td>
                <td>
                  {m.file_url ? (
                    <a
                      href={resolvePublicUrl(m.file_url)}
                      target="_blank"
                      rel="noreferrer"
                      className="action-link"
                    >
                      <strong>{m.title ? titleCase(m.title) : "Open"}</strong>
                    </a>
                  ) : (
                    <strong>{titleCase(m.title)}</strong>
                  )}
                </td>
                <td>{titleCase(m.file_type)}</td>
                <td>{Number(m.is_viewed) === 1 ? "Yes" : "No"}</td>
                {!isVideos ? <td>{Number(m.is_read) === 1 ? "Yes" : "No"}</td> : null}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={isVideos ? 4 : 5}>
                <div className="empty-state" style={{ padding: "16px 8px" }}>
                  No videos or resources in this section.
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function CourseProgressReport() {
  const [courseId] = useState(String(BBA_COURSE_ID));
  const [weeks, setWeeks] = useState([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [weekId, setWeekId] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 25, timezone: "" });

  const [activeRow, setActiveRow] = useState(null);
  const [unlockedWeeks, setUnlockedWeeks] = useState([]);
  const [weeksLoading, setWeeksLoading] = useState(false);
  const [weeksError, setWeeksError] = useState("");
  const [expandedWeekId, setExpandedWeekId] = useState(null);
  const [weekSectionCache, setWeekSectionCache] = useState({});
  const [expandedSectionKey, setExpandedSectionKey] = useState(null);

  useEffect(() => {
    setPage(1);
  }, [query, status, weekId, courseId, perPage]);

  useEffect(() => {
    if (!courseId) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminCourseProgressReportAction({
          course_id: courseId,
          page,
          per_page: perPage,
          search: query.trim() || undefined,
          status: status === "all" ? undefined : status,
          week_id: weekId || undefined
        });
        if (cancelled) return;
        if (!res?.success) {
          setRows([]);
          setMeta({ total: 0, page, per_page: perPage, timezone: "" });
          setError(res?.message || res?.msg || "Failed to load course progress.");
          return;
        }
        setRows(Array.isArray(res.data) ? res.data : []);
        if (Array.isArray(res.weeks)) setWeeks(res.weeks);
        setMeta({
          total: Number(res?.meta?.total || 0),
          page: Number(res?.meta?.page || page),
          per_page: Number(res?.meta?.per_page || perPage),
          timezone: res?.meta?.timezone || "",
          today: res?.meta?.today || ""
        });
      } catch (e) {
        if (cancelled) return;
        setRows([]);
        setError(e?.response?.data?.message || e?.message || "Failed to load course progress.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [courseId, page, perPage, query, status, weekId, reloadNonce]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeWeekModal();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const total = meta?.total ? Number(meta.total) : 0;
  const totalPages = total ? Math.max(1, Math.ceil(total / perPage)) : 1;
  const start = total && rows.length ? (page - 1) * perPage + 1 : 0;
  const end = total && rows.length ? start + rows.length - 1 : 0;

  const pageButtons = useMemo(() => {
    const max = 5;
    const safeTotal = Math.max(1, totalPages);
    const current = Math.min(Math.max(1, page), safeTotal);
    let s = Math.max(1, current - 2);
    let e = Math.min(safeTotal, s + max - 1);
    s = Math.max(1, e - max + 1);
    const arr = [];
    for (let i = s; i <= e; i += 1) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const clearAndReload = () => {
    setQuery("");
    setStatus("all");
    setWeekId("");
    setPage(1);
    setError("");
    setReloadNonce((n) => n + 1);
  };

  function closeWeekModal() {
    setActiveRow(null);
    setUnlockedWeeks([]);
    setWeeksError("");
    setExpandedWeekId(null);
    setWeekSectionCache({});
    setExpandedSectionKey(null);
  }

  async function openProgressModal(row) {
    if (!row?.user_id || isOnboardingPending(row)) return;
    setActiveRow(row);
    setUnlockedWeeks([]);
    setWeeksError("");
    setExpandedWeekId(null);
    setWeekSectionCache({});
    setExpandedSectionKey(null);
    setWeeksLoading(true);
    try {
      const res = await adminUserCourseProgressAction(row.user_id, { page: 1, per_page: 100 });
      if (!res?.success) {
        setWeeksError(res?.message || res?.msg || "Failed to load unlocked weeks.");
        return;
      }
      const list = Array.isArray(res.data) ? res.data : [];
      const forCourse = row.course_id
        ? list.filter((w) => Number(w.course_id) === Number(row.course_id))
        : list;
      setUnlockedWeeks(forCourse);
    } catch (e) {
      setWeeksError(e?.response?.data?.message || e?.message || "Failed to load unlocked weeks.");
    } finally {
      setWeeksLoading(false);
    }
  }

  async function toggleWeekExpand(weekRow) {
    const id = Number(weekRow?.week_id);
    if (!Number.isInteger(id) || id < 1 || !activeRow?.user_id) return;
    setExpandedSectionKey(null);
    if (expandedWeekId === id) {
      setExpandedWeekId(null);
      return;
    }
    if (weekSectionCache[id]?.sections) {
      setExpandedWeekId(id);
      return;
    }
    setWeekSectionCache((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), loading: true, error: "" }
    }));
    try {
      const res = await adminUserCourseWeekSectionsAction(activeRow.user_id, id);
      if (!res?.success) {
        setWeekSectionCache((prev) => ({
          ...prev,
          [id]: {
            loading: false,
            error: res?.message || res?.msg || "Failed to load section progress.",
            week: weekRow,
            sections: []
          }
        }));
        setExpandedWeekId(id);
        return;
      }
      setWeekSectionCache((prev) => ({
        ...prev,
        [id]: {
          loading: false,
          error: "",
          week: res.data?.week || weekRow,
          sections: Array.isArray(res.data?.sections) ? res.data.sections : []
        }
      }));
      setExpandedWeekId(id);
    } catch (e) {
      setWeekSectionCache((prev) => ({
        ...prev,
        [id]: {
          loading: false,
          error: e?.response?.data?.message || e?.message || "Failed to load section progress.",
          week: weekRow,
          sections: []
        }
      }));
      setExpandedWeekId(id);
    }
  }

  function toggleSectionExpand(weekIdValue, sectionId) {
    const key = `${weekIdValue}:${sectionId}`;
    setExpandedSectionKey((prev) => (prev === key ? null : key));
  }

  const listColSpan = 9;

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Course progress unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="toolbar">
        <div className="flex items-center gap-3" style={{ flexWrap: "wrap" }}>
          <div className="search-box">
            <input
              type="text"
              placeholder="Search name, email, week…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <select
              className="form-control"
              style={{ minWidth: 180 }}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              aria-label="Filter by progress status"
            >
              <option value="all">All statuses</option>
              <option value="onboarding">Onboarding</option>
              <option value="not_started">Not started</option>
              <option value="in_progress">In progress</option>
              <option value="waiting">Waiting for next week</option>
              <option value="ready_to_unlock">Ready to unlock</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div className="filter-group">
            <select
              className="form-control"
              style={{ minWidth: 180 }}
              value={weekId}
              onChange={(e) => setWeekId(e.target.value)}
              aria-label="Filter by current week"
            >
              <option value="">All weeks</option>
              {weeks.map((w) => (
                <option key={w.id} value={w.id}>
                  {weekOptionLabel(w)}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <select
              className="form-control w-130"
              value={String(perPage)}
              onChange={(e) => setPerPage(Number(e.target.value))}
              aria-label="Rows per page"
            >
              <option value="10">10 / page</option>
              <option value="25">25 / page</option>
              <option value="50">50 / page</option>
            </select>
          </div>
          <div className="filter-group">
            <button className="btn btn-outline" type="button" onClick={clearAndReload} disabled={loading}>
              ↻ Clear
            </button>
          </div>
        </div>
      </div>
      
      {meta.timezone ? (
        <p className="help-text" style={{ marginTop: 0, marginBottom: 12 }}>
          Unlock dates use midnight in {meta.timezone}
          {meta.today ? ` (today ${meta.today})` : ""}. Core videos do not block the next week.
        </p>
      ) : null}

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Onboarding</th>
                <th>Current week</th>
                <th>Status</th>
                <th>Sections</th>
                <th>Unlocked On</th>
                <th>Next Unlock On</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={listColSpan}>
                    <div className="empty-state" style={{ padding: "28px 12px" }}>
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : rows.length ? (
                rows.map((row) => {
                  const pending = isOnboardingPending(row);
                  const completed = isCourseCompleted(row);
                  return (
                    <tr key={`${row.user_id}-${row.course_id}`}>
                      <td>
                        {row.user_id ? (
                          <Link to={`/users/${row.user_id}`} className="action-link">
                            <strong>{userLabel(row)}</strong>
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{row.user_email || "—"}</td>
                      <td>{titleCase(row.onboarding_status)}</td>
                      <td>
                        {pending ? "—" : row.week_title ? <strong>{titleCase(row.week_title)}</strong> : "—"}
                      </td>
                      <td>
                        <span className={`badge ${progressBadgeClass(row.progress_status)}`}>
                          {row.progress_status_label || titleCase(row.progress_status)}
                        </span>
                      </td>
                      <td>{pending ? "—" : row.sections_label || "—"}</td>
                      <td>{pending ? "—" : formatDate(row.unlocked_at)}</td>
                      <td>{pending || completed ? "—" : formatDate(row.due_date || row.next_week_unlock_at)}</td>
                      <td>
                        <div className="actions-cell">
                          {!pending ? (
                            <button
                              type="button"
                              className="action-link"
                              style={{ background: "transparent", border: "none", padding: 0 }}
                              onClick={() => openProgressModal(row)}
                            >
                              View
                            </button>
                          ) : row.user_id ? (
                            <Link to={`/users/${row.user_id}`} className="action-link">
                              Profile
                            </Link>
                          ) : (
                            "—"
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={listColSpan}>
                    <div className="empty-state">
                      <div className="empty-icon">📈</div>
                      No customers match your filters.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <span className="pagination-info">
            {total ? `Showing ${start}–${end} of ${total}` : "No results"}
          </span>
          <div className="pagination-pages">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page <= 1 || loading}
              title="First page"
              aria-label="First page"
            >
              {"<<"}
            </button>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
              ‹
            </button>
            {pageButtons.map((p) => (
              <button
                key={p}
                className={p === page ? "active" : ""}
                type="button"
                onClick={() => setPage(p)}
                disabled={loading}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
            >
              ›
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages || loading}
              title="Last page"
              aria-label="Last page"
            >
              {">>"}
            </button>
          </div>
        </div>
      </div>

      <div className={`modal-overlay ${activeRow ? "active" : ""}`} onClick={closeWeekModal}>
        <div className="modal modal-xl" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Course Progress</h3>
            <button className="modal-close" type="button" onClick={closeWeekModal}>
              ✕
            </button>
          </div>
          <div className="modal-body">
            {activeRow ? (
              <div>
                <p className="help-text" style={{ marginBottom: 8 }}>
                  {[userLabel(activeRow), titleCase(activeRow.course_title)]
                    .filter((v) => v && v !== "—")
                    .join(" · ")}
                </p>
                <p style={{ marginBottom: 12 }}>
                  <Link to={`/users/${activeRow.user_id}`} className="action-link">
                    Open full profile
                  </Link>
                </p>
                {weeksError ? (
                  <div className="empty-state" style={{ padding: "28px 12px" }}>
                    {weeksError}
                  </div>
                ) : weeksLoading ? (
                  <div className="empty-state" style={{ padding: "28px 12px" }}>
                    Loading…
                  </div>
                ) : (
                  <div className="table-wrapper">
                    <table>
                      <thead>
                        <tr>
                          <th>Week</th>
                          <th>Status</th>
                          <th>Unlocked On</th>
                          <th>Completed On</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unlockedWeeks.length ? (
                          unlockedWeeks.map((weekRow) => {
                            const weekOpen = expandedWeekId === Number(weekRow.week_id);
                            const cached = weekSectionCache[weekRow.week_id] || {};
                            const sections = Array.isArray(cached.sections) ? cached.sections : [];
                            return (
                              <Fragment key={weekRow.id || weekRow.week_id}>
                                <tr className={weekOpen ? "row-expanded" : undefined}>
                                  <td>
                                    <strong>{titleCase(weekRow.week_title)}</strong>
                                  </td>
                                  <td>
                                    <span
                                      className={`badge ${
                                        weekRow.status === "completed" ? "badge-success" : "badge-warning"
                                      }`}
                                    >
                                      {titleCase(weekRow.status_label || weekRow.status)}
                                    </span>
                                  </td>
                                  <td>{formatDate(weekRow.unlocked_at)}</td>
                                  <td>{formatDate(weekRow.completed_at)}</td>
                                  <td>
                                    <div className="actions-cell">
                                      <button
                                        type="button"
                                        className="action-link"
                                        onClick={() => toggleWeekExpand(weekRow)}
                                      >
                                        {weekSectionCache[weekRow.week_id]?.loading
                                          ? "Loading…"
                                          : weekOpen
                                            ? "Hide"
                                            : "View"}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                                <tr className={`row-expand-detail${weekOpen ? " is-open" : ""}`}>
                                  <td colSpan={5}>
                                    <SmoothCollapse open={weekOpen}>
                                      {cached.loading ? (
                                        <div className="empty-state" style={{ padding: "16px 8px" }}>
                                          Loading…
                                        </div>
                                      ) : cached.error ? (
                                        <div className="empty-state" style={{ padding: "16px 8px" }}>
                                          {cached.error}
                                        </div>
                                      ) : (
                                        <div className="nested-table-wrap">
                                          <h4 style={{ marginBottom: 8, fontSize: 16 }}>Sections</h4>
                                          <table className="nested-table">
                                            <thead>
                                              <tr>
                                                <th>#</th>
                                                <th>Section</th>
                                                <th>Type</th>
                                                <th>Status</th>
                                                <th>Watched</th>
                                                <th>Ready</th>
                                                <th>Completed On</th>
                                                <th>Action</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {sections.length ? (
                                                sections.map((s) => {
                                                  const sectionKey = `${weekRow.week_id}:${s.week_section_id}`;
                                                  const sectionOpen = expandedSectionKey === sectionKey;
                                                  return (
                                                    <Fragment key={s.week_section_id}>
                                                      <tr className={sectionOpen ? "row-expanded" : undefined}>
                                                        <td>{s.position ?? "—"}</td>
                                                        <td>
                                                          <strong>{titleCase(s.section_title)}</strong>
                                                          {s.is_core_videos ? (
                                                            <span className="badge badge-muted" style={{ marginLeft: 8 }}>
                                                              Core
                                                            </span>
                                                          ) : null}
                                                        </td>
                                                        <td>{titleCase(s.section_type)}</td>
                                                        <td>
                                                          {s.is_core_videos ? (
                                                            "—"
                                                          ) : (
                                                            <span
                                                              className={`badge ${
                                                                s.status === "Completed"
                                                                  ? "badge-success"
                                                                  : s.status === "In progress"
                                                                    ? "badge-warning"
                                                                    : "badge-muted"
                                                              }`}
                                                            >
                                                              {s.status || "—"}
                                                            </span>
                                                          )}
                                                        </td>
                                                        <td>
                                                          {s.is_core_videos
                                                            ? "—"
                                                            : Number(s.is_watched) === 1
                                                              ? "Yes"
                                                              : "No"}
                                                        </td>
                                                        <td>
                                                          {s.is_core_videos
                                                            ? "—"
                                                            : Number(s.ready_for_next_step) === 1
                                                              ? "Yes"
                                                              : "No"}
                                                        </td>
                                                        <td>{s.is_core_videos ? "—" : formatDate(s.completed_at)}</td>
                                                        <td>
                                                          <div className="actions-cell">
                                                            <button
                                                              type="button"
                                                              className="action-link"
                                                              onClick={() =>
                                                                toggleSectionExpand(weekRow.week_id, s.week_section_id)
                                                              }
                                                            >
                                                              {sectionOpen ? "Hide" : "View"}
                                                            </button>
                                                          </div>
                                                        </td>
                                                      </tr>
                                                      <tr className={`row-expand-detail${sectionOpen ? " is-open" : ""}`}>
                                                        <td colSpan={8}>
                                                          <SmoothCollapse open={sectionOpen}>
                                                            <SectionMediaTable section={s} />
                                                          </SmoothCollapse>
                                                        </td>
                                                      </tr>
                                                    </Fragment>
                                                  );
                                                })
                                              ) : (
                                                <tr>
                                                  <td colSpan={8}>
                                                    <div className="empty-state" style={{ padding: "16px 8px" }}>
                                                      No sections found for this week.
                                                    </div>
                                                  </td>
                                                </tr>
                                              )}
                                            </tbody>
                                          </table>
                                        </div>
                                      )}
                                    </SmoothCollapse>
                                  </td>
                                </tr>
                              </Fragment>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={5}>
                              <div className="empty-state" style={{ padding: "28px 12px" }}>
                                No unlocked weeks found.
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
